// src/pages/pengamatan/destrak/DestrakToolbar.jsx
import React, { useState } from 'react';
import {
    Typography, Stack, TextField, IconButton, Menu, MenuItem, Divider,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Slide, Button, Alert, Snackbar
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import { styled, alpha } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const DestrakToolbar = ({ 
  searchTerm, 
  onSearchChange, 
  onResetFilter, 
  anchorEl, 
  onMenuClick, 
  onMenuClose, 
  open,
  selectedItems,
  userJabatan,
  userRoleNames,
  updateStatusPengamatan,
  onStatusUpdate,
  onReload,
  orders // Tambahkan prop orders untuk mengakses data
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(false);

  // Fungsi untuk mengecek apakah user memiliki akses untuk mengubah status pengamatan
  const canEditStatus = () => {
    // Admin dengan role Destrak atau Losses
    const isAdminWithValidRole = userJabatan === "Admin" && 
      (userRoleNames.includes("Distribusi Size dan Kematangan") ||
      userRoleNames.includes("Persen Bunga") || 
      userRoleNames.includes("Berat Tanaman") ||
      userRoleNames.includes("Buah Alami") ||
      userRoleNames.includes("Losses") ||
      userRoleNames.includes("Destrak"));
    
    // Section Head dengan role Developer
    const isSectionHeadDeveloper = userJabatan === "Section Head" && 
      userRoleNames.includes("Developer");
    
    return isAdminWithValidRole || isSectionHeadDeveloper;
  };

  // Fungsi untuk mengecek apakah semua item yang dipilih memiliki status yang sama dengan status tertentu
  const allSelectedItemsHaveStatus = (status) => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.every(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === status;
    });
  };

  // Fungsi untuk mengecek apakah ada minimal 1 item yang memiliki status Processed
  const hasAnyProcessedItem = () => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.some(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === "Processed";
    });
  };

  // Fungsi untuk mengecek apakah ada minimal 1 item yang memiliki status Approved
  const hasAnyApprovedItem = () => {
    if (selectedItems.length === 0) return false;
    
    return selectedItems.some(itemId => {
      const order = orders.find(order => order.id === itemId);
      return order && order.status_pengamatan === "Approved";
    });
  };

  // Fungsi untuk mengecek apakah semua item yang dipilih sudah memiliki status Draft
  const allSelectedItemsAreDraft = () => {
    return allSelectedItemsHaveStatus("Draft");
  };

  // Fungsi untuk mengecek apakah semua item yang dipilih sudah memiliki status Processed
  const allSelectedItemsAreProcessed = () => {
    return allSelectedItemsHaveStatus("Processed");
  };

  // Fungsi untuk mengecek apakah semua item yang dipilih sudah memiliki status Approved
  const allSelectedItemsAreApproved = () => {
    return allSelectedItemsHaveStatus("Approved");
  };

  // Fungsi untuk mengecek apakah selected items memiliki status campuran (Approved dan Processed)
  const hasMixedApprovedAndProcessed = () => {
    if (selectedItems.length < 2) return false;
    
    const hasApproved = hasAnyApprovedItem();
    const hasProcessed = hasAnyProcessedItem();
    
    return hasApproved && hasProcessed;
  };

  const handleOpenDialog = (action) => {
    setDialogAction(action);
    setConfirmDialogOpen(true);
    onMenuClose();
  };

  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmAction = async () => {
    setLoading(true);
    
    try {
      const success = await updateStatusPengamatan(
        selectedItems, 
        dialogAction, 
        () => {
          // Callback yang akan dipanggil setelah berhasil
          if (onReload) {
            onReload();
          }
        }
      );
      
      if (success) {
        onStatusUpdate({
          open: true,
          message: `Status pengamatan berhasil diubah ke ${dialogAction}. Halaman akan dimuat ulang...`,
          severity: 'success'
        });
      } else {
        onStatusUpdate({
          open: true,
          message: 'Gagal mengubah status pengamatan',
          severity: 'error'
        });
      }
    } catch (error) {
      onStatusUpdate({
        open: true,
        message: 'Terjadi kesalahan saat mengubah status',
        severity: 'error'
      });
      setLoading(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Logika untuk menampilkan menu "Ubah Ke Draft"
  const showDraftMenu = () => {
    // Selalu tampilkan kecuali jika semua item sudah Draft
    return !allSelectedItemsAreDraft();
  };

// Logika untuk menampilkan menu "Ubah Ke Processed"
const showProcessedMenu = () => {
  // Jangan tampilkan jika ada campuran status Approved dan Processed
  if (hasMixedApprovedAndProcessed()) {
    return false;
  }
  
  // Jangan tampilkan jika semua item sudah memiliki status Processed
  if (allSelectedItemsAreProcessed()) {
    return false;
  }
  
  // Tampilkan untuk kasus lainnya (semua Approved, atau kombinasi lainnya)
  return true;
};

  // Cek apakah ada menu status yang akan ditampilkan
  const hasStatusMenu = showDraftMenu() || showProcessedMenu();

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          Data Destrak Tanaman
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Kolom Pencarian */}
          <TextField
            size="small"
            placeholder="Cari Lokasi..."
            value={searchTerm}
            onChange={onSearchChange}
            sx={{ width: 250 }}
          />
          
          {/* Icon Reset Filter */}
          <IconButton
            color="primary"
            size="small"
            onClick={onResetFilter}
            title="Reset Filter"
          >
            <FilterAltOffIcon />
          </IconButton>
          
          {/* Icon Menu Options - Hanya tampil jika ada item yang dipilih */}
          {selectedItems.length > 0 && (
            <IconButton
              color="primary"
              size="small"
              onClick={onMenuClick}
              aria-controls={open ? 'demo-customized-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          
          {/* Menu Options */}
          <StyledMenu
            id="demo-customized-menu"
            slotProps={{
              list: {
                'aria-labelledby': 'demo-customized-button',
              },
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={onMenuClose}
          >
            {/* Menu Ubah Status Pengamatan - Hanya tampil jika user memiliki akses */}
            {canEditStatus() && (
              <>
                {/* Tampilkan menu "Ubah Ke Draft" sesuai logika */}
                {showDraftMenu() && (
                  <MenuItem key="draft" onClick={() => handleOpenDialog('Draft')} disableRipple>
                    <EditIcon />
                    Ubah Ke Draft
                  </MenuItem>
                )}
                
                {/* Tampilkan menu "Ubah Ke Processed" sesuai logika */}
                {showProcessedMenu() && (
                  <MenuItem key="processed" onClick={() => handleOpenDialog('Processed')} disableRipple>
                    <EditIcon />
                    Ubah Ke Processed
                  </MenuItem>
                )}
                
                {/* Tampilkan divider jika ada menu status yang ditampilkan */}
                {hasStatusMenu && (
                  <Divider key="divider" sx={{ my: 0.5 }} />
                )}
              </>
            )}

            <MenuItem onClick={onMenuClose} disableRipple>
              <ArchiveIcon />
              Download Entry
            </MenuItem>
            <MenuItem onClick={onMenuClose} disableRipple>
              <ArchiveIcon />
              Download Form
            </MenuItem>
          </StyledMenu>
        </Stack>
      </Stack>

      {/* Dialog Konfirmasi */}
      <Dialog
        open={confirmDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDialog}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          Konfirmasi Ubah Status
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Apakah Anda yakin ingin mengubah status pengamatan {selectedItems.length} nomor sample ke <strong>{dialogAction}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Batal
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Memproses...' : 'Ya, Ubah Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar untuk Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DestrakToolbar;